import { Suspense } from "react";
import { Outlet, useParams } from "react-router-dom";

import { Skeleton } from "@/components/_atoms/skeleton";
import { SkipLink } from "@/components/_atoms/skip-link";
import { NotFoundView } from "@/components/_molecules/not-found-view";
import { BasePageLayout } from "@/components/_templates/base-page-layout";
import { SidebarSettings } from "@/components/_templates/sidebar-settings/sidebar-settings";
import { I18nProvider } from "@/domain/_shared/i18n/i18n-provider";
import { PreLoader } from "@/domain/_shared/preloader/components/preloader";
import { AppearanceProvider } from "@/domain/user/components/appearance-context";
import { useCurrentUserContext } from "@/domain/user/hooks/use-current-user-context";
import { useWorkspaceSettings } from "@/domain/workspace/hooks/api/use-workspace-settings";
import {
  CurrentWorkspaceContextProvider,
  useCurrentWorkspaceContext,
} from "@/domain/workspace/hooks/useCurrentWorkspaceContext";

const SettingsPageSkeleton = () => {
  return (
    <div className="relative mx-10 mb-16 mt-0 flex grow items-stretch">
      <div className="box-border flex w-full touch-pan-x touch-pan-y flex-col">
        <div className="flex flex-col gap-1.5">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-96" />
        </div>
        <div className="my-6 w-full border-t border-solid" />
        <div className="max-w-xl space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
    </div>
  );
};

const InnerLayout = () => {
  const { currentUser } = useCurrentUserContext();
  const { currentWorkspace } = useCurrentWorkspaceContext();
  const { data: workspaceSettings, loading } = useWorkspaceSettings({
    workspaceId: currentWorkspace.id,
  });

  return (
    <>
      <PreLoader workspaceId={currentWorkspace.id} userId={currentUser.id}>
        <I18nProvider
          workspaceId={currentWorkspace.id}
          userLocale={currentUser.locale}
          workspaceLocale={workspaceSettings?.locale}
        >
          <SkipLink targetId="main-content" />
          <BasePageLayout
            renderSidebar={() => <SidebarSettings workspace={currentWorkspace} />}
            renderContent={() => (
            loading ? (
              <SettingsPageSkeleton />
            ) : (
              <Suspense fallback={<SettingsPageSkeleton />}>
                <Outlet />
              </Suspense>
            )
          )}
          />
        </I18nProvider>
      </PreLoader>
    </>
  );
};

const SettingsContent = () => {
  const { workspaceUrl } = useParams();
  const { currentUser } = useCurrentUserContext();

  if (!workspaceUrl) {
    return <NotFoundView />;
  }

  return (
    <CurrentWorkspaceContextProvider workspaceUrl={workspaceUrl} userId={currentUser.id}>
      <AppearanceProvider>
        <InnerLayout />
      </AppearanceProvider>
    </CurrentWorkspaceContextProvider>
  );
};

export function SettingsRootLayout() {
  return <SettingsContent />;
}
